import React, { useCallback, useRef, useMemo } from 'react';
import { Input } from '../ui/input';
import { cn } from '../../lib/utils';

/**
 * CurrencyInput - Input formatado para valores monetários com máscara em tempo real
 * 
 * Funciona como um controlled input que aplica máscara de moeda automaticamente.
 * Os dígitos são inseridos da direita para esquerda, como uma calculadora.
 * Exemplo: digitar "12345" resulta em "123,45"
 * 
 * @param {Object} props
 * @param {number} props.value - Valor numérico (em unidades, ex: 100.50)
 * @param {Function} props.onChange - Callback com valor numérico
 * @param {string} props.currency - Código da moeda: 'BRL' ou 'USD' (default: 'BRL')
 * @param {string} props.placeholder - Placeholder do input
 * @param {boolean} props.disabled - Desabilitar input
 * @param {string} props.className - Classes CSS adicionais
 * @param {string} props.data-testid - Test ID
 */
export default function CurrencyInput({
  value = 0,
  onChange,
  onFocus,
  onBlur,
  currency = 'BRL',
  placeholder = '0,00',
  disabled = false,
  className = '',
  'data-testid': dataTestId = 'currency-input'
}) {
  const inputRef = useRef(null);

  // Configuração por moeda
  const config = useMemo(() => {
    const configs = {
      BRL: {
        symbol: 'R$',
        locale: 'pt-BR',
        decimalSeparator: ',',
        thousandSeparator: '.'
      },
      USD: {
        symbol: '$',
        locale: 'en-US',
        decimalSeparator: '.',
        thousandSeparator: ','
      }
    };
    return configs[currency] || configs.BRL;
  }, [currency]);

  // Format value (decimal) to display string with mask
  const formatValueToDisplay = useCallback((val) => {
    const cents = Math.round((val || 0) * 100);
    if (cents === 0) return '0,00';
    
    const absValue = Math.abs(cents);
    const integerPart = Math.floor(absValue / 100);
    const decimalPart = absValue % 100;
    
    // Format integer part with thousand separator
    const formattedInteger = integerPart.toString().replace(/\B(?=(\d{3})+(?!\d))/g, config.thousandSeparator);
    
    // Format decimal part with leading zero if needed
    const formattedDecimal = decimalPart.toString().padStart(2, '0');
    
    return `${formattedInteger}${config.decimalSeparator}${formattedDecimal}`;
  }, [config.thousandSeparator, config.decimalSeparator]);

  // Display value - derived directly from props.value
  const displayValue = formatValueToDisplay(value);

  const handleChange = (e) => {
    const inputValue = e.target.value;
    
    // Extract only digits
    const digits = inputValue.replace(/\D/g, '');
    
    // Convert to cents (integer)
    const newCents = parseInt(digits, 10) || 0;
    
    // Limit to a reasonable max value (999,999,999.99)
    const limitedCents = Math.min(newCents, 99999999999);
    
    // Convert cents to decimal value and call onChange
    const decimalValue = limitedCents / 100;
    onChange?.(decimalValue);
  };

  const handleKeyDown = (e) => {
    // Allow: backspace, delete, tab, escape, enter, arrows
    const allowedKeys = ['Backspace', 'Delete', 'Tab', 'Escape', 'Enter', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
    
    if (allowedKeys.includes(e.key)) {
      return;
    }
    
    // Allow Ctrl/Cmd + A, C, V, X
    if ((e.ctrlKey || e.metaKey) && ['a', 'c', 'v', 'x'].includes(e.key.toLowerCase())) {
      return;
    }
    
    // Block non-numeric keys
    if (!/^\d$/.test(e.key)) {
      e.preventDefault();
    }
  };

  return (
    <div className="relative">
      <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm font-medium">
        {config.symbol}
      </span>
      <Input
        ref={inputRef}
        type="text"
        inputMode="decimal"
        value={displayValue}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onFocus={onFocus}
        onBlur={onBlur}
        placeholder={placeholder}
        disabled={disabled}
        className={cn("pl-10", className)}
        data-testid={dataTestId}
      />
    </div>
  );
}

/**
 * Hook para formatar valores monetários
 */
export function useCurrencyFormatter(currency = 'BRL') {
  const format = (value) => {
    if (!value && value !== 0) return '';
    
    const config = {
      BRL: { locale: 'pt-BR', currency: 'BRL' },
      USD: { locale: 'en-US', currency: 'USD' }
    };
    
    const { locale, currency: curr } = config[currency] || config.BRL;
    
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: curr
    }).format(value);
  };

  const symbol = currency === 'USD' ? '$' : 'R$';

  return { format, symbol };
}
